import { useEffect, useRef, useState, type ReactElement } from 'react';
import { bankingApi, invoiceApi, receiptApi, companyApi, type BankAccount, type BankTransaction, type OverdueInvoice, type BankSyncResult, type MatchSuggestion, type Invoice, type Receipt, ApiError } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useToast } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, euro, displayCustomer } from '../ui';
import { MerchantBadge, merchantKey } from '../merchantBadge';

type Tab = 'bank' | 'dunning';

export default function Banking(): ReactElement {
  const [tab, setTab] = useState<Tab>('bank');
  const { active } = useCompany();
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Banking & Mahnwesen" subtitle="Bankkonten, Zahlungseingänge zuordnen und überfällige Rechnungen anmahnen." />
      <div className="mb-6 flex gap-2 border-b border-gray-200">
        {([['bank', 'Banking'], ['dunning', 'Mahnwesen']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {active && tab === 'bank' && <BankTab companyId={active.id} />}
      {active && tab === 'dunning' && <DunningTab companyId={active.id} />}
    </div>
  );
}

function locked(e: unknown): boolean { return e instanceof ApiError && e.status === 402; }
function Upgrade({ text }: { text: string }): ReactElement {
  return <EmptyState title={text} hint="Ab Professional verfügbar." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />;
}

const PROVIDER_LABEL: Record<string, string> = { manual: 'Manuell', paypal: 'PayPal', sumup: 'SumUp', fints: 'Bank (FinTS)', statement: 'Kontoauszug (PDF)' };

interface FintsBank { blz: string; bic: string; name: string; ort: string; url: string }

function BankTab({ companyId }: { companyId: string }): ReactElement {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [providers, setProviders] = useState<Record<string, boolean>>({ manual: true });
  const [sel, setSel] = useState<string | null>(null);
  const [txs, setTxs] = useState<BankTransaction[]>([]);
  const [txQuery, setTxQuery] = useState('');
  const [logos, setLogos] = useState<Record<string, string | null>>({});
  const [suggestions, setSuggestions] = useState<Record<string, MatchSuggestion | null>>({});
  const [automatching, setAutomatching] = useState(false);
  const [matchFor, setMatchFor] = useState<BankTransaction | null>(null); // manuelle Zuordnung
  const [bookFor, setBookFor] = useState<BankTransaction | null>(null); // Direktbuchung (Einnahme/Ausgabe)
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Neues Konto
  const [newName, setNewName] = useState('');
  const [newProvider, setNewProvider] = useState('manual');
  const [bankQuery, setBankQuery] = useState('');
  const [bankHits, setBankHits] = useState<FintsBank[]>([]);
  const [chosenBank, setChosenBank] = useState<FintsBank | null>(null);
  const [fintsUser, setFintsUser] = useState('');
  const [fintsAcct, setFintsAcct] = useState('');
  const [importing, setImporting] = useState(false);
  const stmtFileRef = useRef<HTMLInputElement>(null);
  const [balanceEdit, setBalanceEdit] = useState<string | null>(null); // Roh-Eingabe „1234,56"

  // PIN-Dialog beim Sync
  const [pinFor, setPinFor] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  // TAN-Dialog (FinTS PSD2): nach PIN verlangt die Bank ggf. eine TAN.
  const [tanState, setTanState] = useState<{
    sessionId: string; decoupled: boolean; challenge: string | null; photo: { mimeType: string; dataUrl: string } | null;
  } | null>(null);
  const [tanInput, setTanInput] = useState('');
  const [tanBusy, setTanBusy] = useState(false);
  // Ref spiegelt tanState fuer den Decoupled-Poll-Loop (vermeidet stale closure).
  const tanStateRef = useRef(tanState);
  tanStateRef.current = tanState;

  function loadAccounts() {
    bankingApi.accounts(companyId).then((a) => { setAccounts(a); if (!sel && a[0]) setSel(a[0].id); }).catch(setError);
  }
  useEffect(() => {
    loadAccounts();
    bankingApi.providers(companyId).then(setProviders).catch(() => undefined);
    // Rueckkehr vom OAuth-Redirect (PayPal/SumUp) auswerten und URL bereinigen.
    const search = new URLSearchParams(window.location.search);
    for (const prov of ['paypal', 'sumup'] as const) {
      const p = search.get(prov);
      if (!p) continue;
      const label = prov === 'paypal' ? 'PayPal' : 'SumUp';
      if (p === 'connected') {
        setInfo(`${label} wurde verbunden. Klicke „Umsätze abrufen", um deine Transaktionen zu laden.`);
      } else {
        const msg: Record<string, string> = {
          denied: `Die ${label}-Verbindung wurde abgebrochen.`,
          expired: `Die ${label}-Verbindung ist abgelaufen. Bitte erneut versuchen.`,
          error: `Die ${label}-Verbindung ist fehlgeschlagen. Bitte erneut versuchen.`,
        };
        setError(new Error(msg[p] ?? `${label}-Verbindung fehlgeschlagen.`));
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    /* eslint-disable-next-line */
  }, [companyId]);
  useEffect(() => { setTxQuery(''); if (sel) bankingApi.transactions(companyId, sel).then(setTxs).catch(() => setTxs([])); }, [sel, companyId]);

  // Logos der Gegenparteien laden (Kreis-Badge, global gecacht im Backend).
  useEffect(() => {
    const names = Array.from(new Set(txs.map((t) => t.counterparty?.trim()).filter(Boolean))) as string[];
    const missing = names.filter((n) => !(merchantKey(n) in logos));
    if (missing.length === 0) return;
    bankingApi.merchantLogos(companyId, missing).then((m) => setLogos((p) => ({ ...p, ...m }))).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txs, companyId]);

  // Zuordnungs-Vorschlaege fuer offene Transaktionen laden.
  useEffect(() => {
    const open = txs.filter((t) => t.matchStatus !== 'matched' && !(t.id in suggestions)).slice(0, 30);
    if (open.length === 0) return;
    open.forEach((t) => {
      bankingApi.suggestion(companyId, t.id).then((s) => setSuggestions((p) => ({ ...p, [t.id]: s }))).catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txs, companyId]);

  function reloadTxs() { if (sel) bankingApi.transactions(companyId, sel).then(setTxs).catch(() => undefined); }

  async function acceptSuggestion(t: BankTransaction) {
    const s = suggestions[t.id];
    if (!s) return;
    try { await bankingApi.match(companyId, t.id, s.kind, s.id); reloadTxs(); }
    catch (e) { setError(e); }
  }
  async function unmatch(t: BankTransaction) {
    try { await bankingApi.unmatch(companyId, t.id); setSuggestions((p) => { const c = { ...p }; delete c[t.id]; return c; }); reloadTxs(); }
    catch (e) { setError(e); }
  }
  async function markPrivate(t: BankTransaction) {
    try { await bankingApi.markPrivate(companyId, t.id); reloadTxs(); }
    catch (e) { setError(e); }
  }
  async function runAutomatch() {
    if (!sel) return;
    setAutomatching(true); setError(null); setInfo(null);
    try {
      const n = await bankingApi.automatch(companyId, sel);
      setInfo(n > 0 ? `${n} Umsätze automatisch zugeordnet.` : 'Keine eindeutigen Zuordnungen gefunden.');
      setSuggestions({});
      reloadTxs();
    } catch (e) { setError(e); } finally { setAutomatching(false); }
  }

  // Bank-Suche (debounced-ish)
  useEffect(() => {
    if (newProvider !== 'fints' || bankQuery.trim().length < 2) { setBankHits([]); return; }
    const t = setTimeout(() => { bankingApi.searchBanks(companyId, bankQuery).then(setBankHits).catch(() => setBankHits([])); }, 250);
    return () => clearTimeout(t);
  }, [bankQuery, newProvider, companyId]);

  if (locked(error)) return <Upgrade text="Banking-Integration" />;

  const selAccount = accounts.find((a) => a.id === sel);

  // Gemeinsame Behandlung eines Sync-/TAN-Ergebnisses (fertig ODER TAN noetig).
  function onSyncResult(accountId: string, r: BankSyncResult) {
    if (r.status === 'tan_required') {
      setPinFor(null); setPin('');
      setTanState({ sessionId: r.sessionId, decoupled: r.decoupled, challenge: r.challenge, photo: r.photo });
      setTanInput('');
      // Decoupled (App-Freigabe): automatisch pollen, bis die Bank freigibt.
      if (r.decoupled) void pollDecoupled(accountId, r.sessionId);
      return;
    }
    setInfo(
      `${r.imported} neue Umsätze importiert (${r.total} geprüft)` +
      (r.accounts && r.accounts > 1 ? ` · ${r.accounts} Konten` : '') + '.',
    );
    setPinFor(null); setPin(''); setTanState(null);
    bankingApi.transactions(companyId, accountId).then(setTxs);
    loadAccounts();
  }

  async function runSync(withPin?: string, since?: string) {
    if (!sel) return;
    setError(null); setInfo(null); setSyncing(true);
    try {
      const r = await bankingApi.sync(companyId, sel, withPin, since);
      onSyncResult(sel, r);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'FINTS_PIN_REQUIRED') { setPinFor(sel); }
      else { setError(e); setPinFor(null); setTanState(null); }
    } finally { setSyncing(false); }
  }

  /**
   * Aelteren Zeitraum nachholen. Standardmaessig laedt der Abruf nur seit dem
   * letzten Mal — wer nachtraeglich ein ganzes Jahr braucht, gibt hier den
   * Beginn an.
   */
  async function syncSeit() {
    const heute = new Date();
    const vorschlag = `${heute.getFullYear() - 1}-01-01`;
    const eingabe = window.prompt(
      'Umsätze ab welchem Datum abrufen? (JJJJ-MM-TT)\n\n' +
        'Bereits vorhandene Umsätze werden nicht doppelt angelegt.\n' +
        'PayPal gibt höchstens 3 Jahre zurück heraus.',
      vorschlag,
    );
    if (!eingabe) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(eingabe.trim())) {
      setError(new Error('Bitte ein Datum im Format JJJJ-MM-TT angeben.'));
      return;
    }
    await runSync(undefined, new Date(`${eingabe.trim()}T00:00:00Z`).toISOString());
  }

  // App-Freigabe (decoupled): in Intervallen nachfragen, ob die Bank freigegeben hat.
  async function pollDecoupled(accountId: string, sessionId: string) {
    for (let i = 0; i < 40; i++) { // ~40 * 3s = 2 Min
      await new Promise((res) => setTimeout(res, 3000));
      // Abbruch, wenn der Nutzer den Dialog inzwischen geschlossen hat.
      if (!tanStateRef.current || tanStateRef.current.sessionId !== sessionId) return;
      try {
        const r = await bankingApi.tan(companyId, accountId, sessionId); // ohne TAN
        if (r.status === 'done') { onSyncResult(accountId, r); return; }
        // weiterhin tan_required -> erneut pollen
      } catch (e) {
        if (e instanceof ApiError && e.code === 'FINTS_SESSION_EXPIRED') { setError(e); setTanState(null); return; }
        // transienter Fehler -> weiter versuchen
      }
    }
  }

  async function submitTan() {
    if (!sel || !tanState) return;
    setTanBusy(true); setError(null);
    try {
      const r = await bankingApi.tan(companyId, sel, tanState.sessionId, tanInput.trim());
      onSyncResult(sel, r);
    } catch (e) { setError(e); }
    finally { setTanBusy(false); }
  }

  function startSync() {
    if (!selAccount) return;
    if (selAccount.provider === 'fints') setPinFor(selAccount.id); // PIN abfragen
    else runSync();
  }

  async function removeAccount() {
    if (!selAccount) return;
    if (!window.confirm(`Bankkonto „${selAccount.name}" wirklich löschen? Alle zugehörigen Umsätze werden mit entfernt.`)) return;
    setError(null); setInfo(null);
    try {
      await bankingApi.removeAccount(companyId, selAccount.id);
      setInfo('Bankkonto gelöscht.');
      setSel(null);
      setTxs([]);
      loadAccounts();
    } catch (e) { setError(e); }
  }

  async function connectPaypal() {
    setError(null);
    try {
      const url = await bankingApi.paypalConnectUrl(companyId);
      window.location.href = url; // Redirect zu PayPal (Login + Zustimmung)
    } catch (e) { setError(e); }
  }

  async function connectSumup() {
    setError(null);
    try {
      const url = await bankingApi.sumupConnectUrl(companyId);
      window.location.href = url; // Redirect zu SumUp (Login + Zustimmung)
    } catch (e) { setError(e); }
  }

  // Kontoauszug-PDF hochladen -> parsen -> Konto per IBAN + Transaktionen (wie Sync).
  async function importStatement(file: File) {
    setError(null);
    setImporting(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
        r.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1] ?? '';
      const res = await bankingApi.importStatement(companyId, base64, file.type || 'application/pdf', newName || undefined);
      setShowNew(false); setNewName(''); setNewProvider('manual');
      loadAccounts();
      setSel(res.accountId);
      const dup = res.duplicates > 0 ? ` (${res.duplicates} bereits vorhanden)` : '';
      const warn = res.balanceOk === false ? ' ⚠️ Der Saldo im Auszug passt nicht exakt zur Buchungssumme – bitte prüfen.' : '';
      setInfo(`Kontoauszug importiert: ${res.imported} Buchung(en) übernommen${dup}.${warn}`);
    } catch (e) { setError(e); }
    finally { setImporting(false); if (stmtFileRef.current) stmtFileRef.current.value = ''; }
  }

  // Manuellen Kontostand speichern (Eingabe „1.234,56" -> Cent).
  async function saveBalance(accountId: string) {
    if (balanceEdit === null) return;
    const norm = balanceEdit.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const value = Number(norm);
    if (!Number.isFinite(value)) { setError(new Error('Bitte einen gültigen Betrag eingeben, z. B. 1234,56.')); return; }
    try {
      await bankingApi.setBalance(companyId, accountId, Math.round(value * 100));
      setBalanceEdit(null);
      loadAccounts();
      setInfo('Kontostand aktualisiert.');
    } catch (e) { setError(e); }
  }

  async function createAccount() {
    // PayPal/SumUp: kein generisches Anlegen — der Kunde verbindet sein Konto via OAuth.
    if (newProvider === 'paypal') { await connectPaypal(); return; }
    if (newProvider === 'sumup') { await connectSumup(); return; }
    // Kontoauszug: Datei-Dialog oeffnen; das eigentliche Anlegen passiert im Upload.
    if (newProvider === 'statement') { stmtFileRef.current?.click(); return; }
    const base = { name: newName || chosenBank?.name || 'Konto', provider: newProvider };
    const payload = newProvider === 'fints' && chosenBank
      ? { ...base, fintsUrl: chosenBank.url, fintsBankCode: chosenBank.blz, fintsUser, externalId: fintsAcct || undefined, iban: fintsAcct || undefined }
      : base;
    await bankingApi.addAccount(companyId, payload);
    setShowNew(false); setNewName(''); setNewProvider('manual'); setChosenBank(null); setBankQuery(''); setFintsUser(''); setFintsAcct('');
    loadAccounts();
  }

  return (
    <div className="space-y-4">
      {/* Verstecktes Datei-Feld fuer den Kontoauszug-Import (Anlegen + weitere Auszuege). */}
      <input
        ref={stmtFileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void importStatement(f); }}
      />
      {error ? <ErrorNote error={error} /> : null}
      {info && <div className="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700 ring-1 ring-green-100">{info}</div>}

      <div className="flex flex-wrap items-center gap-2">
        {accounts.map((a) => (
          <button key={a.id} onClick={() => setSel(a.id)} className={`rounded-xl px-3 py-2 text-sm ${sel === a.id ? 'bg-blue-600 text-white' : 'bg-white ring-1 ring-gray-300'}`}>
            {a.name} <span className="opacity-70">· {PROVIDER_LABEL[a.provider] ?? a.provider}</span> · {euro(a.balanceCents)}
          </button>
        ))}
        <Button variant="secondary" onClick={() => setShowNew(true)}>+ Konto</Button>
      </div>

      {showNew && (
        <Card className="space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">Quelle</span>
            <select value={newProvider} onChange={(e) => { setNewProvider(e.target.value); setChosenBank(null); }}
              className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
              {Object.entries(PROVIDER_LABEL).map(([k, l]) => (
                <option key={k} value={k} disabled={!providers[k]}>
                  {l}{!providers[k] && k !== 'manual' ? ' (nicht konfiguriert)' : ''}
                </option>
              ))}
            </select>
          </label>

          {newProvider === 'fints' ? (
            <>
              {!chosenBank ? (
                <div>
                  <Field label="Bank suchen" value={bankQuery} onChange={setBankQuery} placeholder="Name oder BLZ, z. B. Sparkasse Köln" />
                  {bankHits.length > 0 && (
                    <div className="mt-1 max-h-48 overflow-auto rounded-xl border border-gray-200">
                      {bankHits.map((b) => (
                        <button key={b.blz} onClick={() => { setChosenBank(b); setNewName(b.name); }}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-blue-50">
                          <span className="font-medium">{b.name}</span> <span className="text-gray-500">· {b.ort} · BLZ {b.blz}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl bg-blue-50 p-3 text-sm">
                  <div className="font-medium text-gray-900">{chosenBank.name}</div>
                  <div className="text-xs text-gray-500">BLZ {chosenBank.blz} · {chosenBank.ort}</div>
                  <button onClick={() => setChosenBank(null)} className="mt-1 text-xs text-blue-600 hover:underline">andere Bank</button>
                </div>
              )}
              <Field label="Anmeldename / Benutzerkennung" value={fintsUser} onChange={setFintsUser} placeholder="dein Online-Banking-Login" />
              <Field label="Kontonummer / IBAN (optional)" value={fintsAcct} onChange={setFintsAcct} placeholder="leer = erstes Konto" />
              <p className="text-xs text-gray-500">Deine <strong>PIN</strong> wird nicht gespeichert – du gibst sie bei jedem Abruf neu ein.</p>
            </>
          ) : newProvider === 'paypal' ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Du wirst zu <strong>PayPal</strong> weitergeleitet und meldest dich dort mit deinem eigenen
              PayPal-Konto an. Saldify erhält nur <strong>Lesezugriff</strong> auf deine Umsätze – kein Zugriff
              auf dein Passwort.
            </p>
          ) : newProvider === 'sumup' ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Du wirst zu <strong>SumUp</strong> weitergeleitet und meldest dich dort mit deinem eigenen
              SumUp-Konto an. Saldify erhält nur <strong>Lesezugriff</strong> auf deine Umsätze – kein Zugriff
              auf dein Passwort.
            </p>
          ) : newProvider === 'statement' ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Lade den <strong>Kontoauszug als PDF</strong> hoch (z. B. aus deinem Online-Banking). Saldify liest
              die Buchungen automatisch aus und ordnet sie deinem Konto zu – genau wie bei einer Bankverbindung,
              nur ohne Zugangsdaten. Ideal, wenn deine Bank nicht gefunden wird.
            </p>
          ) : (
            <Field label="Kontoname" value={newName} onChange={setNewName} placeholder="z. B. Geschäftskonto" />
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowNew(false)}>Abbrechen</Button>
            <Button onClick={createAccount} busy={importing} disabled={newProvider === 'fints' && (!chosenBank || !fintsUser)}>
              {newProvider === 'paypal' ? 'Mit PayPal verbinden'
                : newProvider === 'sumup' ? 'Mit SumUp verbinden'
                : newProvider === 'statement' ? 'PDF auswählen'
                : 'Anlegen'}
            </Button>
          </div>
        </Card>
      )}

      {selAccount && (
        <div className="rounded-xl bg-gray-50 px-4 py-2 text-sm dark:bg-gray-800">
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-600 dark:text-gray-400">
              {PROVIDER_LABEL[selAccount.provider] ?? selAccount.provider}
              {selAccount.provider !== 'manual' && ` · ${selAccount.lastSyncAt ? `zuletzt: ${new Date(selAccount.lastSyncAt).toLocaleString('de-DE')}` : 'noch nicht abgerufen'}`}
            </span>
            <div className="flex items-center gap-2">
              {txs.some((t) => t.matchStatus !== 'matched') && <Button variant="secondary" onClick={runAutomatch} busy={automatching}>Automatisch zuordnen</Button>}
              <Button variant="secondary" onClick={() => setBalanceEdit(balanceEdit === null ? (selAccount.balanceCents / 100).toFixed(2).replace('.', ',') : null)}>Kontostand</Button>
              {(selAccount.provider === 'paypal' || selAccount.provider === 'sumup') && selAccount.connected === false ? (
                <Button onClick={() => (selAccount.provider === 'paypal' ? connectPaypal() : connectSumup())}>
                  {selAccount.provider === 'paypal' ? 'PayPal verbinden' : 'SumUp verbinden'}
                </Button>
              ) : selAccount.provider === 'statement' ? (
                <Button onClick={() => stmtFileRef.current?.click()} busy={importing}>Weiteren Auszug importieren</Button>
              ) : selAccount.provider !== 'manual' ? (
                <>
                  <Button onClick={startSync} busy={syncing}>Umsätze abrufen</Button>
                  {/* Der normale Abruf holt nur seit dem letzten Mal. Wer ein
                      ganzes Jahr nachtraegen will, waehlt hier den Beginn. */}
                  <Button variant="secondary" onClick={syncSeit} busy={syncing}>Zeitraum nachholen</Button>
                </>
              ) : null}
              <Button variant="secondary" onClick={removeAccount}>Löschen</Button>
            </div>
          </div>
          {balanceEdit !== null && (
            <div className="mt-2 flex items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Aktueller Kontostand (€):</span>
              <input
                value={balanceEdit}
                onChange={(e) => setBalanceEdit(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void saveBalance(selAccount.id); if (e.key === 'Escape') setBalanceEdit(null); }}
                autoFocus
                placeholder="z. B. 3162,86"
                className="w-40 rounded-lg border border-gray-300 px-3 py-1.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:bg-gray-900 dark:text-gray-100"
              />
              <Button onClick={() => void saveBalance(selAccount.id)}>Speichern</Button>
              <Button variant="secondary" onClick={() => setBalanceEdit(null)}>Abbrechen</Button>
              <span className="text-xs text-gray-500">Setzt den Saldo absolut (z. B. Anfangsbestand). Ein späterer Auszug-Import überschreibt ihn nicht.</span>
            </div>
          )}
        </div>
      )}

      {/* PIN-Dialog fuer FinTS-Sync */}
      {pinFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">Banking-PIN eingeben</h2>
            <p className="mt-1 text-sm text-gray-600">Deine PIN wird nur für diesen Abruf verwendet und nicht gespeichert.</p>
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} autoFocus
              className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setPinFor(null); setPin(''); }}>Abbrechen</Button>
              <Button onClick={() => runSync(pin)} busy={syncing} disabled={!pin}>Abrufen</Button>
            </div>
          </div>
        </div>
      )}

      {/* TAN-Dialog (FinTS PSD2): App-Freigabe (decoupled) ODER TAN-Eingabe/photoTAN */}
      {tanState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 dark:bg-gray-800">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {tanState.decoupled ? 'Freigabe in deiner Banking-App' : 'TAN eingeben'}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {tanState.challenge || (tanState.decoupled
                ? 'Bitte bestätige den Zugriff in deiner Banking-App. Wir warten auf die Freigabe …'
                : 'Bitte die TAN deiner Bank eingeben.')}
            </p>

            {/* photoTAN-Bild (falls die Bank eines liefert) */}
            {tanState.photo && (
              <img src={tanState.photo.dataUrl} alt="TAN-Grafik" className="mx-auto mt-4 max-h-48 rounded-lg border border-gray-200" />
            )}

            {tanState.decoupled ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-500" />
                Warten auf Freigabe …
              </div>
            ) : (
              <input
                value={tanInput}
                onChange={(e) => setTanInput(e.target.value)}
                inputMode="numeric"
                autoFocus
                placeholder="TAN"
                className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setTanState(null); setTanInput(''); }}>Abbrechen</Button>
              {!tanState.decoupled && (
                <Button onClick={submitTan} busy={tanBusy} disabled={!tanInput.trim()}>Bestätigen</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {sel && txs.length > 0 && (
        <input
          value={txQuery}
          onChange={(e) => setTxQuery(e.target.value)}
          placeholder="Umsätze durchsuchen (Name, Verwendungszweck)…"
          className="mb-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
        />
      )}

      {sel && (() => {
        const q = txQuery.trim().toLowerCase();
        const shown = q
          ? txs.filter((t) => `${t.counterparty ?? ''} ${t.purpose ?? ''}`.toLowerCase().includes(q))
          : txs;
        return txs.length === 0 ? <EmptyState title="Keine Transaktionen" hint="Rufe Umsätze ab oder erfasse sie manuell." />
        : shown.length === 0 ? <EmptyState title="Kein Treffer" hint={`Keine Buchung passt zu „${txQuery}“.`} />
        : (
          <div className="space-y-2">
            {shown.map((t) => {
              const name = t.counterparty || t.purpose || 'Buchung';
              const sug = suggestions[t.id];
              return (
              <Card key={t.id} className="!p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <MerchantBadge name={name} logo={t.counterparty ? logos[merchantKey(t.counterparty)] : null} />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900 dark:text-gray-100">{name}</div>
                      {t.purpose && t.counterparty && <div className="truncate text-sm text-gray-600 dark:text-gray-400">{t.purpose}</div>}
                      <div className="text-xs text-gray-500 dark:text-gray-400">{new Date(t.date).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                  <span className={`shrink-0 whitespace-nowrap font-semibold ${t.amountCents < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{euro(t.amountCents)}</span>
                </div>

                {/* Zuordnungs-Zeile */}
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 text-xs dark:border-gray-700">
                  {t.matchStatus === 'matched' ? (
                    <>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">✓ zugeordnet ({t.matchedInvoiceId ? 'Rechnung' : 'Beleg'})</span>
                      <button onClick={() => unmatch(t)} className="text-gray-500 hover:underline">lösen</button>
                    </>
                  ) : t.matchStatus === 'private' ? (
                    <>
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                        🏠 privat ({t.amountCents > 0 ? 'Privateinlage' : 'Privatentnahme'})
                      </span>
                      <button onClick={() => unmatch(t)} className="text-gray-500 hover:underline">lösen</button>
                    </>
                  ) : sug ? (
                    <>
                      <span className="text-gray-500">Vorschlag: <span className="font-medium text-gray-700 dark:text-gray-300">{sug.label}</span> <span className="text-gray-400">({sug.reason})</span></span>
                      <button onClick={() => acceptSuggestion(t)} className="rounded-lg bg-blue-600 px-2 py-1 font-medium text-white hover:bg-blue-700">Übernehmen</button>
                      <button onClick={() => setMatchFor(t)} className="text-gray-500 hover:underline">anderes wählen</button>
                      <button onClick={() => setBookFor(t)} className="text-emerald-600 hover:underline">{t.amountCents > 0 ? 'als Einnahme buchen' : 'als Ausgabe buchen'}</button>
                      <button onClick={() => markPrivate(t)} className="text-violet-600 hover:underline">privat</button>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-400">offen — keine automatische Zuordnung</span>
                      <button onClick={() => setMatchFor(t)} className="text-blue-600 hover:underline">manuell zuordnen</button>
                      <button onClick={() => setBookFor(t)} className="text-emerald-600 hover:underline">{t.amountCents > 0 ? 'als Einnahme buchen' : 'als Ausgabe buchen'}</button>
                      <button onClick={() => markPrivate(t)} className="text-violet-600 hover:underline">als privat buchen</button>
                    </>
                  )}
                </div>
              </Card>
              );
            })}
          </div>
        );
      })()}

      {matchFor && (
        <ManualMatchModal
          companyId={companyId}
          tx={matchFor}
          onClose={() => setMatchFor(null)}
          onMatched={() => { setMatchFor(null); reloadTxs(); }}
        />
      )}

      {bookFor && (
        <DirectBookModal
          companyId={companyId}
          tx={bookFor}
          onClose={() => setBookFor(null)}
          onBooked={() => { setBookFor(null); reloadTxs(); loadAccounts(); }}
        />
      )}
    </div>
  );
}

// Manuelle Zuordnung: je nach Richtung Ausgangsrechnungen (Eingang) oder
// Belege/Eingangsrechnungen (Ausgang) zur Auswahl, mit Suche.
function ManualMatchModal({ companyId, tx, onClose, onMatched }: { companyId: string; tx: BankTransaction; onClose: () => void; onMatched: () => void }): ReactElement {
  const isIncome = tx.amountCents > 0;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isIncome) invoiceApi.list(companyId, { type: 'invoice' }).then(setInvoices).catch(() => undefined);
    else receiptApi.list(companyId).then((rs) => setReceipts(rs.filter((r) => r.direction === 'expense'))).catch(() => undefined);
  }, [companyId, isIncome]);

  async function pick(kind: 'invoice' | 'receipt', id: string) {
    setBusy(true);
    try { await bankingApi.match(companyId, tx.id, kind, id); onMatched(); } finally { setBusy(false); }
  }

  const ql = q.trim().toLowerCase();
  const invList = invoices.filter((i) => !ql || `${i.number ?? ''} ${i.recipientName ?? ''}`.toLowerCase().includes(ql));
  const recList = receipts.filter((r) => !ql || `${r.vendor ?? ''} ${r.invoiceNumber ?? ''} ${r.description ?? ''}`.toLowerCase().includes(ql));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 dark:bg-gray-800">
        <h2 className="mb-1 text-lg font-bold text-gray-900 dark:text-gray-100">
          {isIncome ? 'Ausgangsrechnung zuordnen' : 'Beleg / Eingangsrechnung zuordnen'}
        </h2>
        <p className="mb-3 text-sm text-gray-500">{tx.counterparty || tx.purpose} · {euro(tx.amountCents)}</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…"
          className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        <div className="max-h-[50vh] divide-y divide-gray-100 overflow-auto dark:divide-gray-700">
          {isIncome ? (
            invList.length === 0 ? <p className="py-6 text-center text-sm text-gray-500">Keine Rechnungen.</p> :
            invList.map((i) => (
              <button key={i.id} disabled={busy} onClick={() => pick('invoice', i.id)}
                className="flex w-full items-center justify-between gap-2 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-gray-700">
                <span className="min-w-0 truncate">{i.number ?? 'Entwurf'}{i.recipientName ? ` · ${i.recipientName}` : ''} <span className="text-xs text-gray-400">{i.status}</span></span>
                <span className="shrink-0 font-medium">{euro(i.grossCents)}</span>
              </button>
            ))
          ) : (
            recList.length === 0 ? <p className="py-6 text-center text-sm text-gray-500">Keine Belege.</p> :
            recList.map((r) => (
              <button key={r.id} disabled={busy} onClick={() => pick('receipt', r.id)}
                className="flex w-full items-center justify-between gap-2 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-gray-700">
                <span className="min-w-0 truncate">{r.vendor || r.description || 'Beleg'}{r.invoiceNumber ? ` · ${r.invoiceNumber}` : ''}</span>
                <span className="shrink-0 font-medium">{euro(r.grossCents)}</span>
              </button>
            ))
          )}
        </div>
        <div className="mt-4 flex justify-end"><Button variant="secondary" onClick={onClose}>Schließen</Button></div>
      </div>
    </div>
  );
}

// Direktbuchung: Bank-Transaktion ohne Beleg als Betriebseinnahme/-ausgabe
// buchen (z.B. Cashback, Zinsen, Bankgebuehren). Erzeugt einen Eigenbeleg.
function DirectBookModal({ companyId, tx, onClose, onBooked }: { companyId: string; tx: BankTransaction; onClose: () => void; onBooked: () => void }): ReactElement {
  const isIncome = tx.amountCents > 0;
  const gross = Math.abs(tx.amountCents);
  const [accounts, setAccounts] = useState<{ number: string; name: string }[]>([]);
  const [accountNumber, setAccountNumber] = useState('');
  const [taxRate, setTaxRate] = useState(0); // Default 0% (Cashback/Zinsen = kein Leistungsentgelt)
  const [isKu, setIsKu] = useState(false); // Kleinunternehmer -> keine USt-Auswahl
  const [description, setDescription] = useState((tx.counterparty || tx.purpose || '').slice(0, 160));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    bankingApi.successAccounts(companyId, isIncome ? 'revenue' : 'expense')
      .then((a) => {
        setAccounts(a);
        // Sinnvoller Default: bei Einnahme 0% -> steuerfreies/§19-Erlöskonto, sonst erstes.
        const prefer = isIncome
          ? a.find((x) => /8195|4185|8200|4200/.test(x.number)) ?? a[0]
          : a[0];
        if (prefer) setAccountNumber(prefer.number);
      })
      .catch(() => undefined);
    // KU-Status fuer das Buchungsjahr laden -> bei KU USt ausblenden + auf 0 zwingen.
    const year = new Date(tx.date).getFullYear();
    companyApi.kuStatus(companyId, year)
      .then((s) => { if (s.isKleinunternehmer) { setIsKu(true); setTaxRate(0); } })
      .catch(() => undefined);
  }, [companyId, isIncome, tx.date]);

  const net = taxRate > 0 ? Math.round(gross / (1 + taxRate / 100)) : gross;
  const taxCents = gross - net;

  async function submit() {
    if (!accountNumber) { setErr('Bitte ein Konto wählen.'); return; }
    setBusy(true); setErr(null);
    try {
      await bankingApi.bookTransaction(companyId, tx.id, { accountNumber, taxRate, ...(description ? { description } : {}) });
      onBooked();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Buchung fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800">
        <h2 className="mb-1 text-lg font-bold text-gray-900 dark:text-gray-100">
          {isIncome ? 'Als Betriebseinnahme buchen' : 'Als Betriebsausgabe buchen'}
        </h2>
        <p className="mb-4 text-sm text-gray-500">{tx.counterparty || tx.purpose} · {euro(tx.amountCents)}</p>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{isIncome ? 'Erlöskonto' : 'Aufwandskonto'}</span>
          <select value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
            <option value="" disabled>Konto wählen…</option>
            {accounts.map((a) => <option key={a.number} value={a.number}>{a.number} · {a.name}</option>)}
          </select>
        </label>

        {isKu ? (
          <div className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            Kleinunternehmer §19 – ohne Umsatzsteuer.
          </div>
        ) : (
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">USt-Satz</span>
            <div className="flex gap-2">
              {[0, 7, 19].map((r) => (
                <button key={r} type="button" onClick={() => setTaxRate(r)}
                  className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium ${taxRate === r ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                  {r}%
                </button>
              ))}
            </div>
          </label>
        )}

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Beschreibung</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z. B. N26 Cashback"
            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        </label>

        <div className="mb-4 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-gray-900 dark:text-gray-400">
          <div className="flex justify-between"><span>Netto</span><span>{euro(isIncome ? net : -net)}</span></div>
          {taxRate > 0 && <div className="flex justify-between"><span>{isIncome ? 'USt' : 'Vorsteuer'} {taxRate}%</span><span>{euro(isIncome ? taxCents : -taxCents)}</span></div>}
          <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-medium text-gray-800 dark:border-gray-700 dark:text-gray-200"><span>Brutto</span><span>{euro(tx.amountCents)}</span></div>
        </div>

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} busy={busy} disabled={!accountNumber}>Buchen</Button>
        </div>
      </div>
    </div>
  );
}

function DunningTab({ companyId }: { companyId: string }): ReactElement {
  const toast = useToast();
  const [invoices, setInvoices] = useState<OverdueInvoice[]>([]);
  const [error, setError] = useState<unknown>(null);

  function load() { bankingApi.overdue(companyId).then(setInvoices).catch(setError); }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  if (locked(error)) return <Upgrade text="Mahnwesen" />;

  // Mahnen: Mail an den Kunden nur, wenn eine Adresse hinterlegt ist. Ohne
  // Mail wird die Stufe trotzdem dokumentiert (Versand dann per Post/manuell).
  async function dun(inv: OverdueInvoice) {
    const mail = inv.customer?.email?.trim();
    const res = await bankingApi.dun(companyId, inv.id, mail ? { sendMail: true, to: mail } : {});
    if (mail && res.mail?.sent) toast.success(`Mahnung an ${res.mail.to} versendet.`);
    else if (mail) toast.error(res.mail?.reason ?? 'Mahnstufe erhöht, aber die E-Mail ging nicht raus.');
    else toast.success('Mahnstufe erhöht. Ohne hinterlegte E-Mail wurde nichts versendet.');
    load();
  }

  return invoices.length === 0 ? (
    <EmptyState title="Keine überfälligen Rechnungen 🎉" hint="Alles im grünen Bereich." />
  ) : (
    <div className="space-y-2">
      {error ? <ErrorNote error={error} /> : null}
      {invoices.map((inv) => (
        <Card key={inv.id} className="flex items-center justify-between !p-4">
          <div>
            <div className="font-medium text-gray-900">{inv.number} · {inv.customer ? displayCustomer(inv.customer) : inv.recipientName}</div>
            <div className="text-xs text-red-600">
              Fällig {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('de-DE') : ''}
              {inv.dunnings[0]
                ? ` · ${inv.dunnings[0].level}. Mahnung ${inv.dunnings[0].sentAt ? 'versendet' : 'erfasst (nicht versendet)'}`
                : ' · noch nicht gemahnt'}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-semibold">{euro(inv.grossCents)}</span>
            <Button onClick={() => dun(inv)}>{inv.dunnings[0] ? 'Nächste Mahnstufe' : 'Mahnen'}</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
